import { useMutation, useQuery } from "@apollo/client/react";
import {
  CreateExpertiseDocument,
  DeleteExpertiseDocument,
  MyselfExpertiseDataDocument,
  UpdateExpertiseDocument,
  type MyselfExpertiseDataQuery,
} from "@/graphql/generated/operations";
import { audienceRelations, type Audience } from "@/lib/audience";

// "Expertise skill" disambiguates the genesis Expertise entity (free-form
// skill descriptors with level + position) from the broader "expertise"
// page domain that also includes experience, education, etc.
export interface SkillFormInput {
  description: string;
  position: number;
  level: number;
  sharedWith?: ReadonlyArray<Audience | string>;
}

export type SkillItem = NonNullable<
  NonNullable<MyselfExpertiseDataQuery["myself"]>["expertises"]
>[number];

export interface UseExpertiseSkillsResult {
  items: readonly SkillItem[];
  add: (input: SkillFormInput) => Promise<void>;
  update: (id: string, input: SkillFormInput) => Promise<void>;
  remove: (id: string) => Promise<void>;
  busy: boolean;
  error: Error | null;
}

function toDataInput(input: SkillFormInput) {
  return {
    description: input.description,
    position: input.position,
    level: input.level,
    ...(input.sharedWith !== undefined
      ? { sharedWith: audienceRelations(input.sharedWith) }
      : {}),
  };
}

export function useExpertiseSkills(): UseExpertiseSkillsResult {
  const query = useQuery(MyselfExpertiseDataDocument);
  const personId = query.data?.myself?.id;

  const [createMut, createState] = useMutation(CreateExpertiseDocument, {
    refetchQueries: [{ query: MyselfExpertiseDataDocument }],
  });
  const [updateMut, updateState] = useMutation(UpdateExpertiseDocument, {
    refetchQueries: [{ query: MyselfExpertiseDataDocument }],
  });
  const [deleteMut, deleteState] = useMutation(DeleteExpertiseDocument, {
    refetchQueries: [{ query: MyselfExpertiseDataDocument }],
  });

  async function add(input: SkillFormInput) {
    if (!personId) throw new Error("useExpertiseSkills: no personId yet");
    await createMut({
      variables: {
        input: { data: { ...toDataInput(input), person: { id: personId } } },
      },
    });
  }

  async function update(id: string, input: SkillFormInput) {
    await updateMut({ variables: { id, input: { data: toDataInput(input) } } });
  }

  async function remove(id: string) {
    await deleteMut({ variables: { input: { id } } });
  }

  return {
    items: query.data?.myself?.expertises ?? [],
    add,
    update,
    remove,
    busy: createState.loading || updateState.loading || deleteState.loading,
    error:
      (createState.error as Error | undefined) ??
      (updateState.error as Error | undefined) ??
      (deleteState.error as Error | undefined) ??
      null,
  };
}
